import type { TFunction } from 'i18next';
import { z } from 'zod';

/** Construit le schéma partagé par les formulaires de création et d'édition d'un produit. */
export function buildCustomShopProductFormSchema(t: TFunction) {
  const productImageSchema = z.custom<File>(
    (value) => value instanceof File && value.type.startsWith('image/'),
    t('admin.shops.products.form.file.image.invalid')
  );
  /** Valide stock et seuil d'alerte : entier positif, null (illimité) ou absent (optionnel). */
  const nullableStockSchema = (field: 'stock' | 'low.stock.alert') =>
    z
      .number({ invalid_type_error: t(`admin.shops.products.form.${field}.invalid`) })
      .int(t(`admin.shops.products.form.${field}.integer`))
      .min(0, t(`admin.shops.products.form.${field}.min`))
      .nullable()
      .optional();

  return z.object({
    name: z
      .string()
      .trim()
      .min(1, t('admin.shops.products.form.name.required'))
      .max(150, t('admin.shops.products.form.name.max')),
    description: z.string().trim().optional(),
    price: z
      .number({ invalid_type_error: t('admin.shops.products.form.price.invalid') })
      .min(0, t('admin.shops.products.form.price.min'))
      .optional(),
    tva: z
      .number({ invalid_type_error: t('admin.shops.products.form.tva.invalid') })
      .min(0, t('admin.shops.products.form.tva.min'))
      .max(100, t('admin.shops.products.form.tva.max')),
    shippingPrice: z
      .number({ invalid_type_error: t('admin.shops.products.form.shipping.price.invalid') })
      .min(0, t('admin.shops.products.form.shipping.price.min')),
    categoryId: z.number().nullable().optional(),
    brandId: z.number().nullable().optional(),
    code: z.string().trim().optional(),
    codeDetail: z.string().trim().optional(),
    howtoUrl: z
      .string()
      .trim()
      .refine((value) => value === '' || z.string().url().safeParse(value).success, {
        message: t('admin.shops.products.form.howto.url.invalid')
      })
      .optional(),
    images: z.array(productImageSchema).optional(),
    designationCom: z
      .string()
      .trim()
      .max(255, t('admin.shops.products.form.designation.com.max'))
      .optional(),
    ean: z.string().trim().max(20, t('admin.shops.products.form.ean.max')).optional(),
    stock: nullableStockSchema('stock'),
    lowStockAlert: nullableStockSchema('low.stock.alert')
  });
}

export type CustomShopProductFormValues = z.infer<
  ReturnType<typeof buildCustomShopProductFormSchema>
>;
